import React, { useCallback, useEffect, useRef, useState } from "react";

import { copyTextToClipboard } from "../../data/clipboard";
import { useAuth } from "../../hooks/useAuth";
import {
  disconnectAllAgents,
  listGrants,
  revokeGrant,
  type AgentGrant,
} from "../../services/agentsService";
// My Files' theme tokens: the page renders outside the .excalidraw container,
// where the editor's CSS variables don't exist.
import "../my-files/my-files.scss";
import "./agents.scss";

/**
 * The body shared by the `/agents` page and the in-app drawer: connect an
 * agent (the prompt to paste into any MCP client) and see / disconnect the
 * signed-in user's OAuth grants. Auth gating mirrors My Files: `authResolved`
 * keeps the sign-in prompt from flashing at a returning user.
 *
 * Section order follows what the user is here for: with agents connected,
 * the list leads and "Connect another agent" folds away below it; with none,
 * connecting leads and the empty state sits under it.
 */

/** The one line the user pastes into their agent. */
export const CONNECT_PROMPT =
  "Follow the instructions at https://mindmap.so/agent-setup.md to connect Mindmap to this agent.";

// Claude Code starts OAuth from `mcp login` (or the first tool call of an
// interactive session), never from `claude -p`.
export const CLAUDE_CODE_ADD =
  "claude mcp add --transport http mindmap https://mindmap.so/mcp";
export const CLAUDE_CODE_LOGIN = "claude mcp login mindmap";

/** Shown once under the clients that have not been through the login end to end. */
export const NOT_TESTED_NOTE = "Not tested end to end yet.";

interface ClientNote {
  name: string;
  commands: string[];
  note: string;
}

// Claude Code first: it is the one client proven end to end.
const CLIENT_NOTES: ClientNote[] = [
  {
    name: "Claude Code",
    commands: [CLAUDE_CODE_ADD, CLAUDE_CODE_LOGIN],
    note: "A browser tab opens to ask for your consent; then the agent can list and edit your maps.",
  },
  {
    name: "Codex",
    commands: [
      "codex mcp add mindmap --url https://mindmap.so/mcp",
      "codex mcp login mindmap",
    ],
    note: NOT_TESTED_NOTE,
  },
  {
    name: "Cursor",
    commands: [
      '{ "mcpServers": { "mindmap": { "url": "https://mindmap.so/mcp" } } }',
    ],
    note: NOT_TESTED_NOTE,
  },
];

/** Scope → the word on the pill. */
const SCOPE_LABELS: Record<string, string> = {
  "mindmap:read": "Read",
  "mindmap:write": "Edit",
};

const formatConnectedAt = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Inline-confirm control shared by every Disconnect: idle → confirm → busy →
// gone, or error with retry.
type ConfirmStep =
  | { status: "idle" }
  | { status: "confirm" }
  | { status: "busy" }
  | { status: "error"; message: string };

const DisconnectControl: React.FC<{
  label: string;
  question: string;
  danger?: boolean;
  onConfirm: () => Promise<void>;
}> = ({ label, question, danger, onConfirm }) => {
  const [step, setStep] = useState<ConfirmStep>({ status: "idle" });

  const run = async () => {
    setStep({ status: "busy" });
    try {
      await onConfirm();
      // The row (or list) unmounts on success; nothing to reset.
    } catch (error) {
      setStep({ status: "error", message: (error as Error).message });
    }
  };

  if (step.status === "idle") {
    return (
      <button
        type="button"
        className={`agents__btn${danger ? " agents__btn--danger" : ""}`}
        onClick={() => setStep({ status: "confirm" })}
      >
        {label}
      </button>
    );
  }
  if (step.status === "busy") {
    return (
      <span className="agents__muted" role="status">
        Disconnecting…
      </span>
    );
  }
  const failed = step.status === "error";
  return (
    <span className="agents__confirm">
      {failed ? (
        <span role="alert" className="agents__error">
          Couldn&apos;t disconnect. {step.message}
        </span>
      ) : (
        <span className="agents__question">{question}</span>
      )}
      <span className="agents__confirm-actions">
        <button
          type="button"
          className="agents__btn agents__btn--danger"
          onClick={run}
        >
          {failed ? "Try again" : "Yes"}
        </button>
        <button
          type="button"
          className="agents__btn"
          onClick={() => setStep({ status: "idle" })}
        >
          Cancel
        </button>
      </span>
    </span>
  );
};

const CopyPromptButton: React.FC = () => {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    },
    [],
  );

  const copy = async () => {
    const ok = await copyTextToClipboard(CONNECT_PROMPT);
    setState(ok ? "copied" : "failed");
    if (timer.current) {
      clearTimeout(timer.current);
    }
    if (ok) {
      timer.current = setTimeout(() => setState("idle"), 2000);
    }
  };

  return (
    <button
      type="button"
      className={`agents__btn agents__btn--primary${
        state === "copied" ? " agents__btn--copied" : ""
      }`}
      onClick={() => void copy()}
    >
      {state === "copied"
        ? "Copied"
        : state === "failed"
        ? "Copy failed — select the text"
        : "Copy"}
    </button>
  );
};

const SignInPrompt: React.FC<{ showBackLink: boolean }> = ({
  showBackLink,
}) => {
  const { signIn } = useAuth();
  return (
    <div className="agents__sign-in" data-testid="agents-sign-in-prompt">
      <h2 className="agents__sign-in-title">Sign in to manage your agents</h2>
      <p className="agents__lead">
        Agents connect to the maps in your account, so you need to be signed in
        to connect one or see what is connected.
      </p>
      <button
        type="button"
        className="agents__btn agents__btn--primary"
        onClick={() => signIn("notion", { returnHere: true })}
      >
        Sign in
      </button>
      {showBackLink && (
        <a href="/" className="agents__link">
          Back to Mindmap.so
        </a>
      )}
    </div>
  );
};

/** The prompt, its copy button and the per-client notes. */
const ConnectBody: React.FC = () => (
  <>
    <p className="agents__lead">
      Paste this into your agent. It fetches the setup page, registers Mindmap,
      and asks you to sign in.
    </p>
    <div className="agents__prompt">
      <code data-testid="agents-prompt" className="agents__prompt-text">
        {CONNECT_PROMPT}
      </code>
      <CopyPromptButton />
    </div>
    <ul className="agents__clients">
      {CLIENT_NOTES.map((client) => (
        <li key={client.name} className="agents__client">
          <div className="agents__client-head">
            <span className="agents__client-name">{client.name}</span>
          </div>
          {client.commands.map((command) => (
            <code key={command} className="agents__command">
              {command}
            </code>
          ))}
          <p className="agents__client-note">{client.note}</p>
        </li>
      ))}
    </ul>
  </>
);

type Grants =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; rows: AgentGrant[] };

export const AgentsPanel: React.FC<{
  /** The page offers a way back to the canvas; the drawer is already on it. */
  context?: "page" | "drawer";
}> = ({ context = "page" }) => {
  const { session, authResolved } = useAuth();
  const isSignedIn = authResolved && !!session;
  // Supabase rotates the access token hourly and on focus. Calls read the
  // current one from here, so a rotation never re-runs the fetch effect (which
  // would flip the list to Loading and collapse any open confirm).
  const accessTokenRef = useRef(session?.access_token ?? "");
  accessTokenRef.current = session?.access_token ?? "";

  const [grants, setGrants] = useState<Grants>({ status: "loading" });
  // Bumped by "Try again" to re-run the fetch.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!isSignedIn) {
      return;
    }
    let cancelled = false;
    setGrants({ status: "loading" });
    listGrants(accessTokenRef.current)
      .then((rows) => !cancelled && setGrants({ status: "ready", rows }))
      .catch(
        (error: Error) =>
          !cancelled && setGrants({ status: "error", message: error.message }),
      );
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, attempt]);

  const disconnectOne = useCallback(async (id: string) => {
    await revokeGrant(accessTokenRef.current, id);
    setGrants((current) =>
      current.status === "ready"
        ? { status: "ready", rows: current.rows.filter((g) => g.id !== id) }
        : current,
    );
  }, []);

  const disconnectAll = useCallback(async () => {
    await disconnectAllAgents(accessTokenRef.current);
    setGrants({ status: "ready", rows: [] });
  }, []);

  if (!authResolved) {
    return (
      <p className="agents__muted" role="status">
        Loading…
      </p>
    );
  }
  if (!isSignedIn) {
    return <SignInPrompt showBackLink={context === "page"} />;
  }
  // The order below depends on the answer, so nothing else is laid out until
  // it is in; otherwise a returning user would watch the sections swap.
  if (grants.status === "loading") {
    return (
      <p className="agents__muted" role="status">
        Loading connected agents…
      </p>
    );
  }

  const hasAgents = grants.status === "ready" && grants.rows.length > 0;

  let list: React.ReactNode;
  if (grants.status === "error") {
    list = (
      <div className="agents__load-error">
        <p role="alert" className="agents__error">
          Couldn&apos;t load your agents. {grants.message}
        </p>
        <button
          type="button"
          className="agents__btn"
          onClick={() => setAttempt((n) => n + 1)}
        >
          Try again
        </button>
      </div>
    );
  } else if (!hasAgents) {
    list = <p className="agents__empty">No agents connected yet.</p>;
  } else {
    list = (
      <>
        <ul className="agents__list" data-testid="agents-list">
          {grants.rows.map((grant) => (
            <li key={grant.id} className="agents__row">
              <div className="agents__row-main">
                <div className="agents__row-name">{grant.client_name}</div>
                <div className="agents__row-meta">
                  <span>Connected {formatConnectedAt(grant.created_at)}</span>
                  <span className="agents__pills">
                    {grant.scopes.map((scope) => (
                      <span key={scope} className="agents__pill">
                        {SCOPE_LABELS[scope] ?? scope}
                      </span>
                    ))}
                  </span>
                </div>
              </div>
              <div className="agents__row-actions">
                <DisconnectControl
                  label="Disconnect"
                  question={`Disconnect ${grant.client_name}?`}
                  onConfirm={() => disconnectOne(grant.id)}
                />
              </div>
            </li>
          ))}
        </ul>
        <div className="agents__all">
          <DisconnectControl
            label="Disconnect all"
            question="Disconnect all agents?"
            danger
            onConfirm={disconnectAll}
          />
        </div>
      </>
    );
  }

  const connected = (
    <section className="agents__section" aria-labelledby="agents-connected">
      <h2 id="agents-connected" className="agents__h2">
        Connected agents
      </h2>
      <p className="agents__lead">
        Each one acts as you. Disconnecting makes its next call ask for your
        consent again.
      </p>
      {list}
    </section>
  );

  if (hasAgents) {
    return (
      <>
        {connected}
        <details
          className="agents__section agents__disclosure"
          data-testid="agents-connect-disclosure"
        >
          <summary id="agents-connect" className="agents__h2 agents__summary">
            Connect another agent
          </summary>
          <ConnectBody />
        </details>
      </>
    );
  }
  return (
    <>
      <section className="agents__section" aria-labelledby="agents-connect">
        <h2 id="agents-connect" className="agents__h2">
          Connect an agent
        </h2>
        <ConnectBody />
      </section>
      {connected}
    </>
  );
};

export default AgentsPanel;
